import React from 'react';
import PropTypes from 'prop-types';
import ReactTable from 'react-table';
import { formatRoute } from 'react-router-named-routes';
import { withTranslation } from 'react-i18next';
// material-ui icons
import Edit from '@material-ui/icons/Edit';
import Check from '@material-ui/icons/Check';
import Erase from '@material-ui/icons/Delete';
// material-ui/ core components
import Checkbox from '@material-ui/core/Checkbox';
import withStyles from '@material-ui/core/styles/withStyles';
// core components
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
import GridItem from 'components/UI/Grid/GridItem';
import { Zoom, Tooltip } from '@material-ui/core';
// style
import extendedTablesStyle from 'assets/jss/material-dashboard-pro-react/views/extendedTablesStyle';
// routers
import { PERMISSION } from 'routes/paths';
import RouterContext from 'helpers/routerContext';
import Paginator from 'components/common/Paginator';
import ModalConfirmation from 'components/common/ModalConfirmation';
import WithPermission from 'components/User/WithPermission';
import { LoadingAnimation } from 'components/common/Loading';
import Snackbar from 'components/UI/Snackbar/Snackbar';

/**
 * @class
 * @extends React.Component
 */
class PermissionGroupList extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    this.state = {
      selectedItems: [],
      modalIsOpen: false,
      itemsToRemove: [],
      groupTitle: '',
    };
  }

  /**
   * @description
   * Open destroying modal.
   *
   * @param {Array} itemsToRemove - Ids of the selected items.
   * @param {string} groupTitle - Title of the selected item.
   */
  handleOpenModal = (itemsToRemove, groupTitle) => {
    this.setState({
      itemsToRemove,
      groupTitle,
      modalIsOpen: true,
    });
  };

  /**
   * @description
   * Callback to close destroying modal if user close it manually.
   */
  handleCloseModal = () => {
    this.setState({ modalIsOpen: false });
  };

  /**
   * @description
   * Handle submit to confirm destroying selected items.
   */
  handleSubmit = () => {
    const { itemsToRemove } = this.state;
    const { permissionDelete } = this.props;

    permissionDelete({ ids: itemsToRemove });

    this.setState({
      itemsToRemove: [],
      modalIsOpen: false,
    });
  };

  /**
   * @description
   * Redirect to permission/create route.
   */
  handleCreate = () => {
    const history = this.context;
    history.push(PERMISSION.CREATE);
  };

  /**
   * @description
   * Redirect to permission/update route.
   *
   * @param {number} id - Id of the permissionGroup to update.
   */
  handleUpdate = id => {
    const history = this.context;
    history.push(formatRoute(PERMISSION.UPDATE, { id }));
  };

  /**
   * @description
   * Redirect to permission/read route.
   *
   * @param {number} id - Id of the permissionGroup to read.
   */
  handleRead = id => {
    const history = this.context;
    history.push(formatRoute(PERMISSION.DETAILS, { id }));
  };

  /**
   * @description
   * Description of ReactTable columns.
   *
   * @param {Array} edges - All permissions groups from api.
   */
  renderColumns = edges => {
    const { selectedItems } = this.state;
    const { classes, t } = this.props;
    return [
      {
        Header: (
          <Checkbox
            onClick={() => {
              this.handleSelectedAll(edges);
            }}
            checked={edges.length === selectedItems.length}
            checkedIcon={<Check className={classes.checkedIcon} />}
            icon={<Check className={classes.uncheckedIcon} />}
            classes={{
              checked: classes.checked,
            }}
            id="allCheckbox"
          />
        ),
        accessor: 'checkbox',
        sortable: false,
        filterable: false,
        maxWidth: 58,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('permissions.list.headers.name')}
          </span>
        ),
        accessor: 'name',
        sortable: false,
        filterable: false,
        minWidth: 150,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('permissions.list.headers.actions')}
          </span>
        ),
        accessor: 'actions',
        sortable: false,
        filterable: false,
      },
      {
        expander: true,
        show: false,
      },
    ];
  };

  /**
   * @description
   * Map the data from api to ReactTable rows.
   *
   * @param {Array} edges - All permissions groups from api.
   * @returns {*} - Rows to reactable.
   */
  renderRows = edges => {
    const { selectedItems } = this.state;
    const { classes, t } = this.props;

    const rows = edges.map(({ node }) => ({
      checkbox: (
        <Checkbox
          onClick={() => {
            this.handleSelectItem(node.id);
          }}
          checked={selectedItems.indexOf(node.id) !== -1}
          checkedIcon={<Check className={classes.checkedIcon} />}
          icon={<Check className={classes.uncheckedIcon} />}
          classes={{
            checked: classes.checked,
          }}
        />
      ),
      id: node.id,
      name: (
        <WithPermission
          alt={<span>{node.name}</span>}
          allowPermissions={['view_group']}
        >
          <a
            className={['read', classes.userInfoDetail].join(' ')}
            href={formatRoute(PERMISSION.DETAILS, { id: node.id })}
            onClick={e => {
              e.preventDefault();
              this.handleRead(node.id);
            }}
          >
            {node.name}
          </a>
        </WithPermission>
      ),
      actions: selectedItems.indexOf(node.id) === -1 && (
        // we've added some custom button actions
        <div className="actions-right">
          <WithPermission allowPermissions={['change_group']}>
            <WithPermission allowPermissions={['view_group']}>
              {/* use this button to update the data row */}
              <Tooltip
                placement="top"
                TransitionComponent={Zoom}
                title={
                  <React.Fragment>
                    {t('common.edit')}
                    <span className={classes.arrow} />
                  </React.Fragment>
                }
                classes={{
                  tooltip: classes.bootstrapTooltip,
                  tooltipPlacementTop: classes.bootstrapPlacementTop,
                }}
              >
                <Button
                  justIcon
                  round
                  simple
                  onClick={() => {
                    this.handleUpdate(node.id);
                  }}
                  color="info"
                  className="update"
                >
                  <Edit />
                </Button>
              </Tooltip>
            </WithPermission>
          </WithPermission>
          {/* use this button to delete the data row */}
          <WithPermission allowPermissions={['delete_group']}>
            <Tooltip
              placement="top"
              TransitionComponent={Zoom}
              title={
                <React.Fragment>
                  {t('common.erase')}
                  <span className={classes.arrow} />
                </React.Fragment>
              }
              classes={{
                tooltip: classes.bootstrapTooltip,
                tooltipPlacementTop: classes.bootstrapPlacementTop,
              }}
            >
              <Button
                justIcon
                round
                simple
                onClick={() => this.handleOpenModal([node.id], node.name)}
                color="danger"
                className="delete"
              >
                <Erase />
              </Button>
            </Tooltip>
          </WithPermission>
        </div>
      ),
    }));
    return rows;
  };

  /**
   * @description
   * Map Loading index Rows with itemProcessing.
   *
   * @param {Object} itemProcessing - Ids of items that is processing.
   * @param {Object} edges - Edges of connection data.
   */
  mapLoadingRows = (itemProcessing, edges) => {
    const loadingRows = {};
    edges.forEach((value, index) => {
      if (itemProcessing.includes(value.node.id)) {
        loadingRows[index] = true;
      }
    });
    return loadingRows;
  };

  /**
   * @description
   * Select all items.
   *
   * @param {Array} edges - All ads from api.
   * @returns {undefined} No return.
   */
  handleSelectedAll(edges) {
    const { selectedItems } = this.state;
    // if all are selected
    if (edges.length === selectedItems.length) {
      this.setState({
        selectedItems: [],
      });
    } else {
      this.setState({
        selectedItems: edges.map(item => item.node.id),
      });
    }
  }

  /**
   * @description
   * Select an item.
   *
   * @param {number} id - Id of selected item.
   * @returns {undefined} No return.
   */
  handleSelectItem(id) {
    const { selectedItems } = this.state;
    // if item is not selected
    if (selectedItems.indexOf(id) !== -1) {
      this.setState({
        selectedItems: selectedItems.filter(item => item !== id),
      });
    } else {
      this.setState({
        selectedItems: [...selectedItems, id],
      });
    }
  }

  /**
   * @description
   * Render Paginator and ReactTable components.
   */
  render() {
    const {
      data,
      changeNumberOfRows,
      changePage,
      numberOfRowsSelected,
      classes,
      itemProcessing,
      t,
      failedIds,
      showNotification,
      closeNotification,
    } = this.props;
    const { modalIsOpen, selectedItems, groupTitle } = this.state;
    const edges = data.permissionsGroups.edges || [];
    const rows = this.renderRows(edges);
    const columns = this.renderColumns(edges);
    const loadingRows = this.mapLoadingRows(itemProcessing, edges);
    return (
      <React.Fragment>
        <Snackbar
          place="tc"
          color="danger"
          message={t(`common.failed_bulk_action`, {
            count: failedIds.length,
          })}
          open={showNotification}
          closeNotification={closeNotification}
          close
        />

        <ModalConfirmation
          show={modalIsOpen}
          confirm={() => this.handleSubmit()}
          cancel={() => this.handleCloseModal()}
          title={
            <span>
              {selectedItems.length > 0
                ? t('common.delete.group_plural')
                : t('common.delete.group')}
            </span>
          }
          description={groupTitle}
          confirmLabel={`${t('common.delete.group_confirm')} ${
            selectedItems.length > 0 ? 's' : ''
          }`}
        />

        <div className={classes.pageContainer}>
          <GridContainer justify="center">
            <GridItem xs={12} sm={9} md={10} lg={8}>
              <GridContainer justify="space-between" alignItems="center">
                <GridItem xs={12} sm={6}>
                  <h3 className={classes.title}>
                    {t('permissions.list.title')}
                  </h3>
                </GridItem>
                <WithPermission allowPermissions={['add_group']}>
                  <WithPermission allowPermissions={['view_group']}>
                    <GridItem xs={12} sm={6}>
                      <GridContainer justify="flex-end">
                        <GridItem>
                          <Button
                            color="success"
                            default
                            onClick={this.handleCreate}
                            id="createPermissionGroup"
                          >
                            {t('permissions.list.create')}
                            <span style={{ marginLeft: 4 }}>
                              {t('permissions.list.create_break')}
                            </span>
                          </Button>
                        </GridItem>
                      </GridContainer>
                    </GridItem>
                  </WithPermission>
                </WithPermission>
              </GridContainer>

              <div className={classes.contentContainer}>
                <GridContainer>
                  <GridItem xs={12}>
                    <GridContainer justify="flex-end">
                      <GridItem
                        xs={12}
                        sm={12}
                        md={7}
                        className={[
                          classes.loteActionsAnimation,
                          !selectedItems.length > 0
                            ? classes.showLote
                            : classes.hideLote,
                        ].join(' ')}
                      >
                        <Paginator
                          pageInfo={data.permissionsGroups.pageInfo}
                          changePage={changePage}
                          changeNumberOfRows={changeNumberOfRows}
                          numberOfRowsSelected={numberOfRowsSelected}
                        />
                      </GridItem>
                    </GridContainer>
                  </GridItem>

                  <GridItem
                    className={[
                      classes.groupActions,
                      classes.loteActionsAnimation,
                      selectedItems.length > 0
                        ? classes.showLote
                        : classes.hideLote,
                    ].join(' ')}
                    xs={12}
                  >
                    <WithPermission allowPermissions={['delete_group']}>
                      <Button
                        color="danger"
                        simple
                        onClick={() => this.handleOpenModal(selectedItems)}
                        id="erasePermissionGroup"
                      >
                        <Erase />
                        {t('common.erase')}
                      </Button>
                    </WithPermission>
                  </GridItem>

                  <GridItem xs={12}>
                    <ReactTable
                      data={rows}
                      filterable
                      columns={columns}
                      defaultPageSize={numberOfRowsSelected}
                      showPaginationTop={false}
                      showPaginationBottom={false}
                      className={['-striped', '-highlight', classes.tbl].join(
                        ' ',
                      )}
                      pageSize={
                        edges.length < numberOfRowsSelected
                          ? edges.length
                          : numberOfRowsSelected
                      }
                      expanded={loadingRows}
                      SubComponent={() => (
                        <div className={classes.loading}>
                          <LoadingAnimation show />
                        </div>
                      )}
                      getTrProps={(state, rowInfo) => ({
                        style: {
                          backgroundColor:
                            rowInfo &&
                            selectedItems.indexOf(rowInfo.original.id) !== -1 &&
                            'rgba(171,221,250,.8)',
                          borderLeft:
                            rowInfo &&
                            failedIds.indexOf(rowInfo.original.id) !== -1
                              ? '4px solid red'
                              : '',
                        },
                      })}
                      noDataText={t('common.no_data')}
                      minRows={8}
                    />

                    <GridContainer justify="flex-end">
                      <GridItem xs={12} sm={12} md={7}>
                        <Paginator
                          pageInfo={data.permissionsGroups.pageInfo}
                          changePage={changePage}
                          changeNumberOfRows={changeNumberOfRows}
                          numberOfRowsSelected={numberOfRowsSelected}
                        />
                      </GridItem>
                    </GridContainer>
                  </GridItem>
                </GridContainer>
              </div>
            </GridItem>
          </GridContainer>
        </div>
      </React.Fragment>
    );
  }
}

/**
 * PermissionList PropTypes
 * @param data - prop adminUsers query's results
 * @param numberOfRowsSelected - prop current number of rows selected
 * @param t - prop from HOC with string translations
 * @param failedIds - id of items that was failed after bulk action
 * @param showNotification - prop to know if notification will be showed or not
 * @callback permissionDelete - callback to remove an element
 * @callback changePage - prop pass to Paginator
 * @callback changeNumberOfRows - prop pass to Paginator
 * @callback closeNotification - callback to close notification
 */
PermissionGroupList.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  itemProcessing: PropTypes.instanceOf(Array).isRequired,
  data: PropTypes.shape({}).isRequired,
  numberOfRowsSelected: PropTypes.number.isRequired,
  permissionDelete: PropTypes.func.isRequired,
  changePage: PropTypes.func.isRequired,
  changeNumberOfRows: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  failedIds: PropTypes.instanceOf(Array).isRequired,
  showNotification: PropTypes.bool.isRequired,
  closeNotification: PropTypes.func.isRequired,
};

// to fix: The withStyles HOC set contextTypes to a children component
// and it have problem with contextType assigned to PermissionList component
// This is a small hack, with intermediary Component to resolved it temporally
const PermissionGroupWrapper = props => <PermissionGroupList {...props} />;

export default withTranslation()(
  withStyles(extendedTablesStyle)(PermissionGroupWrapper),
);
